/**
 * Axios 请求配置
 */
import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import { message } from 'antd';

// API配置
const AUTH_API_BASE = import.meta.env.VITE_AUTH_API_BASE || 'http://117.21.200.53:8522/api';
const AUTH_API_KEY = import.meta.env.VITE_AUTH_API_KEY || 'sk_IW5WLUGjLu1TXdrHxE7MYKMXuXHbP0';

// 响应数据结构
export interface ApiResponse<T = any> {
  success: boolean;
  message?: string;
  error?: string;
  data?: T;
}

/**
 * 创建鉴权服务的 axios 实例
 */
const createAuthClient = (): AxiosInstance => {
  const instance = axios.create({
    baseURL: AUTH_API_BASE,
    timeout: 10000,
    headers: {
      'Content-Type': 'application/json',
      'X-API-Key': AUTH_API_KEY,
    },
  });

  // 请求拦截器
  instance.interceptors.request.use(
    (config) => {
      // 可以在这里添加额外的请求头
      return config;
    },
    (error: AxiosError) => {
      console.error('请求错误:', error);
      return Promise.reject(error);
    }
  );

  // 响应拦截器
  instance.interceptors.response.use(
    (response: AxiosResponse<ApiResponse>) => {
      const { data } = response;

      // API 返回 success: false 时提示错误
      if (data.success === false) {
        const errorMsg = data.error || data.message || '请求失败';
        message.error(errorMsg);
        return Promise.reject(new Error(errorMsg));
      }

      return response;
    },
    (error: AxiosError<ApiResponse>) => {
      // 处理HTTP错误
      if (error.response) {
        const { status, data } = error.response;

        switch (status) {
          case 401:
            message.error(data?.message || 'API密钥无效或已过期');
            break;
          case 403:
            message.error(data?.message || '权限不足');
            break;
          case 404:
            message.error('请求的资源不存在');
            break;
          case 500:
            message.error('服务器错误，请稍后重试');
            break;
          default:
            message.error(data?.message || data?.error || `请求失败 (${status})`);
        }
      } else if (error.request) {
        // 请求已发出但没有收到响应
        message.error('网络连接失败，请检查网络');
      } else {
        // 其他错误
        message.error(error.message || '请求失败');
      }

      return Promise.reject(error);
    }
  );

  return instance;
};

// 导出鉴权API客户端
export const authClient = createAuthClient();

/**
 * 通用请求方法
 */
export const request = {
  get: <T = any>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> =>
    authClient.get<ApiResponse<T>>(url, config).then((res) => res.data),

  post: <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> =>
    authClient.post<ApiResponse<T>>(url, data, config).then((res) => res.data),

  put: <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> =>
    authClient.put<ApiResponse<T>>(url, data, config).then((res) => res.data),

  delete: <T = any>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> =>
    authClient.delete<ApiResponse<T>>(url, config).then((res) => res.data),
};

export default request;
